<template>
    <section
        class="embed-responsive embed-responsive-16by9"
        @keyup="keyup"
    >
        <div
            :id="id"
            class="embed-responsive-item"
        />
    </section>
</template>

<script>
const PROGRESS_INTERVAL = 10000
const JUMP_LENGTH = 5 // 5 seconds

import { KEY_CODES } from "~/lib/constants"
import uniqueId from "lodash/uniqueId"
import { notify } from "~/lib/bugsnag"

// Documentation: https://developers.google.com/youtube/iframe_api_reference
export default {
    inject: ["error"],

    props: {
        code: String,
        url: String,
        autoplay: {
            type: Boolean,
            default: true,
        },

        canAdvance: {
            type: Boolean,
            default: true,
        },

        initialTime: {
            type: Number,
            default: 0,
        },

        trackProgress: {
            type: Boolean,
            default: false,
        },
    },

    emits: ["accessed", "progress"],

    data() {
        return {
            player: null,
            paused: true,
            finished: false,
            time: this.initialTime,
            duration: 0,
            id: uniqueId(),
        }
    },

    watch: {
        trackProgress(track) {
            if (!track) {
                this.stopProgressTimer()
            }
        },
    },

    mounted() {
        /* global YT */
        this.player = new YT.Player(this.id, {
            height: "100%",
            width: "100%",
            videoId: this.code || this.url,
            playerVars: {
                rel: 0, // Show related videos only from the same channel
                playsinline: 1,
                modestbranding: 1,
                autoplay: this.autoplay ? 1 : 0,
                start: this.initialTime || 0,
                controls: this.canAdvance ? 1 : 0,
                disablekb: this.canAdvance ? 0 : 1,
                origin: window.location.origin,
            },

            events: {
                onReady: event => {
                    this.duration = event.target.getDuration()
                    this.$emit("accessed")
                },

                onStateChange: event => {
                    switch (event.data) {
                        case -1: // unstarted
                            break

                        case 0: // ended
                            this.finished = true
                            break

                        case 2: // paused
                            this.paused = true
                            this.stopProgressTimer()
                            this.trackProgress && this.sendProgress()
                            break

                        case 3: // buffering
                        case 5: // video cued
                            break

                        case 1: // playing
                            this.paused = false
                            this.stopProgressTimer()
                            this.startProgressTimerIfNeeded()
                            break

                        default:
                            throw new Error(`Unknown state ${event.data}`)
                    }
                },

                onError: event => {
                    let error
                    switch (event.data) {
                        case 2:
                            error = this.$t("errors.youtube.2")
                            break

                        case 5:
                            error = this.$t("errors.youtube.5")
                            break

                        case 100:
                            error = this.$t("errors.youtube.100")
                            break

                        case 101:
                        case 150:
                            error = this.$t("errors.youtube.101")
                            break

                        default:
                            error = `Unknown state ${event.data}`
                    }
                    this.error(error)
                    notify(new Error(error))
                },
            },
        })
    },

    beforeUnmount() {
        if (this.player && this.trackProgress) {
            this.sendProgress({ isClosing: true })
        }
        this.stopProgressTimer()
        if (this.player) {
            this.player.destroy()
        }
    },

    methods: {
        startProgressTimerIfNeeded() {
            if (!this.finished && !this._timer && this.trackProgress) {
                this._timer = window.setInterval(
                    this.sendProgress,
                    PROGRESS_INTERVAL
                )
            }
        },

        stopProgressTimer() {
            window.clearInterval(this._timer)
            this._timer = null
        },

        playToggle() {
            this.paused ? this.play() : this.pause()
        },

        pause() {
            if (!this.player) return

            this.player.pauseVideo()
            this.paused = true
        },

        play() {
            if (!this.player) return

            this.player.playVideo()
            this.paused = false
        },

        jumpBackwards() {
            this.time = this.player.getCurrentTime()
            this.player && this.player.seekTo
                ? this.player.seekTo(this.time - JUMP_LENGTH)
                : ""
        },

        jumpForward() {
            this.time = this.player.getCurrentTime()
            this.player && this.player.seekTo
                ? this.player.seekTo(this.time + JUMP_LENGTH)
                : ""
        },

        sendProgress({ isClosing } = {}) {
            if (!this.trackProgress) return

            this.time = this.player.getCurrentTime?.() ?? this.time
            let progress = 0
            if (this.finished) {
                progress = 100
            } else if (this.time) {
                progress = (100 * this.time) / this.duration
            }

            this.$emit("progress", {
                progress,
                playBackTime: this.paused ? 0 : PROGRESS_INTERVAL / 1000,
                currentTime: this.time,
                isClosing,
            })
        },

        keyup(event) {
            switch (event.which) {
                case KEY_CODES.SPACE:
                    event.preventDefault()
                    this.playToggle()
                    break

                case KEY_CODES.LEFT:
                    this.canAdvance ? this.jumpBackwards() : ""
                    break

                case KEY_CODES.RIGHT:
                    this.canAdvance ? this.jumpForward() : ""
                    break

                default:
                    break
            }
        },
    },
}
</script>
